import { useEffect, useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import Reveal from "@/components/site/Reveal";

const DEPTH_IMG =
  "https://images.unsplash.com/photo-1549074862-6173e20d02a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxkZWVwJTIwb2NlYW4lMjB1bmRlcndhdGVyJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3ODU3NzgzMDF8MA&ixlib=rb-4.1.0&q=85";

const STATS = [
  { value: 30000, suffix: " ft", label: "Max logging depth reached" },
  { value: 4200, suffix: "+", label: "Downhole runs completed" },
  { value: 27, suffix: "", label: "Offshore rigs served" },
  { value: 99.4, suffix: "%", label: "Job delivery uptime", decimals: 1 },
];

function Counter({ value, suffix = "", decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const duration = 1600;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(value * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  const formatted =
    decimals > 0
      ? display.toFixed(decimals)
      : Math.round(display).toLocaleString("en-US");

  return (
    <span ref={ref} className="font-display font-black tabular-nums">
      {formatted}
      {suffix}
    </span>
  );
}

export default function Capabilities() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section
      id="capabilities"
      ref={ref}
      className="relative overflow-hidden border-b border-white/10 py-24 md:py-40"
      data-testid="capabilities-section"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0 scale-110">
        <img
          src={DEPTH_IMG}
          alt="Deep ocean water"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-background/85" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1600px] px-5 md:px-10">
        <Reveal className="max-w-3xl mb-16 md:mb-24">
          <span className="mono-label text-primary">/ By the numbers</span>
          <h2 className="mt-6 font-display font-bold uppercase tracking-tight text-4xl md:text-6xl leading-[0.9]">
            Depth, measured.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
          {STATS.map((s, i) => (
            <Reveal
              key={s.label}
              delay={i * 0.08}
              className="border-b border-r border-white/10 p-8 md:p-10 bg-background/40 backdrop-blur-sm"
            >
              <div className="text-5xl md:text-6xl text-foreground leading-none mb-6">
                <Counter value={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
              </div>
              <p className="mono-label text-muted-foreground leading-relaxed">
                {s.label}
              </p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
